import { ReactNode, useRef, forwardRef, useImperativeHandle, useState, Ref, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@global/utils/tailwind'

export interface SidePanelInterface {
  handleToggleSidePanel: () => void
}

interface SidePanelProps {
  children: ReactNode
  width?: number | string
  zIndex?: number
  getToggleStatus?: (isOpen: boolean) => void
}

export const SidePanel = forwardRef<SidePanelInterface, SidePanelProps>(
  (
    { children, width = 390, zIndex = 999, getToggleStatus }: SidePanelProps,
    ref: Ref<SidePanelInterface>,
  ) => {
    const sidebarRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const [isSidePanelOpen, setSidePanelOpen] = useState(false)

    // Temporary solution for handle status of side panel
    useEffect(() => {
      if (getToggleStatus) {
        getToggleStatus(isSidePanelOpen)
      }
    }, [isSidePanelOpen, getToggleStatus])

    useImperativeHandle(
      ref,
      () => ({
        handleToggleSidePanel: () => {
          setSidePanelOpen(prev => !prev)
        },
      }),
      [isSidePanelOpen],
    )

    return (
      <AnimatePresence>
        {isSidePanelOpen && (
          <motion.div
            ref={sidebarRef}
            className={cn(
              'absolute right-0 top-0 bottom-0 bg-white shadow-xl flex flex-col mt-[2px]',
              'border-l border-gray-200',
            )}
            style={{ zIndex, width }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div ref={contentRef} className="flex-1 overflow-y-auto flex flex-col" id="side-panel">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  },
)

SidePanel.displayName = 'CustomSidePanel'
